import { Link, useLocation } from "wouter";
import { ChevronDown, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useBrand } from "@/contexts/brand-context";
import { useEffect } from "react";

const PORTAL_BRANDS = [
  { name: "Sustainable Engineering Alliance", slug: "sustainable-engineering-alliance", color: "bg-emerald-500", activeRing: "ring-emerald-500", activeBorder: "border-b-2 border-emerald-400", activeText: "text-emerald-300" },
  { name: "Disti",  slug: "disti",  color: "bg-blue-500", activeRing: "ring-blue-500", activeBorder: "border-b-2 border-blue-400", activeText: "text-blue-300" },
  { name: "Devkit", slug: "devkit", color: "bg-violet-500", activeRing: "ring-violet-500", activeBorder: "border-b-2 border-violet-400", activeText: "text-violet-300" },
];

const BRAND_ACCENT: Record<string, { banner: string; strip: string }> = {
  "sustainable-engineering-alliance": { banner: "bg-emerald-950 border-t-2 border-emerald-500", strip: "border-t-2 border-emerald-500" },
  "disti": { banner: "bg-blue-950 border-t-2 border-blue-500", strip: "border-t-2 border-blue-500" },
  "devkit": { banner: "bg-violet-950 border-t-2 border-violet-500", strip: "border-t-2 border-violet-500" },
};

interface Props {
  isCollapsed: boolean;
  onCollapse: () => void;
}

export function BrandPortalStrip({ isCollapsed, onCollapse }: Props) {
  const { activeBrand, activeBrandMeta, setBrand } = useBrand();
  const [location] = useLocation();

  useEffect(() => {
    const match = location.match(/^\/brand\/(sustainable-engineering-alliance|disti|devkit)$/);
    if (match) setBrand(match[1]);
  }, [location]);

  const accent = activeBrand ? BRAND_ACCENT[activeBrand] : null;

  return (
    <>
      {/* ── Mobile ── */}
      <div className={`md:hidden bg-zinc-900 dark:bg-zinc-950 text-zinc-300 ${accent?.strip ?? ""}`}>
        <div className="mx-auto flex items-center max-w-7xl px-4 h-9 gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-1.5 text-xs font-semibold text-zinc-300 hover:text-white transition-colors"
                data-testid="button-brand-network-mobile"
              >
                {activeBrandMeta ? (
                  <span className={`text-xs font-semibold ${PORTAL_BRANDS.find(b => b.slug === activeBrand)?.activeText ?? "text-white"}`}>
                    {activeBrandMeta.shortName}
                  </span>
                ) : "Brand network"}
                <ChevronDown className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64 p-0" data-testid="dropdown-brand-network-mobile">
              <div className="px-3 py-2 bg-muted/50 border-b">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Our Brand Family</p>
              </div>
              <div className="py-1">
                {PORTAL_BRANDS.map(b => (
                  <DropdownMenuItem key={b.slug} data-testid={`mobile-brand-network-${b.slug}`}
                    onSelect={() => setBrand(activeBrand === b.slug ? null : b.slug)}
                    className="flex items-center gap-3 px-3 py-2 cursor-pointer"
                  >
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${b.color}`} />
                    <span className="text-sm">{b.name}</span>
                    {activeBrand === b.slug && <span className="ml-auto text-xs text-muted-foreground">Active</span>}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                {activeBrand && (
                  <DropdownMenuItem onSelect={() => setBrand(null)} className="px-3 py-2 text-xs text-red-500 cursor-pointer">
                    × Show all content
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild>
                  <Link href="/brand/future-manufacturing" className="flex items-center px-3 py-2 text-xs text-muted-foreground hover:text-foreground cursor-pointer">
                    View all brands →
                  </Link>
                </DropdownMenuItem>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {activeBrand && activeBrandMeta && (
            <button
              onClick={() => setBrand(null)}
              className="ml-auto flex items-center gap-1 text-xs text-zinc-400 hover:text-white"
              data-testid="button-clear-brand-mobile"
            >
              <X className="h-3 w-3" />
              Show all
            </button>
          )}
        </div>
      </div>

      {/* ── Desktop: collapsible strip ── */}
      <div
        className={`hidden md:block bg-zinc-900 dark:bg-zinc-950 text-zinc-300 overflow-hidden transition-[max-height] duration-300 ease-in-out ${
          isCollapsed ? "max-h-0" : "max-h-24"
        } ${accent?.strip ?? ""}`}
        aria-hidden={isCollapsed}
      >
        <div className="mx-auto flex items-center max-w-7xl px-4 h-10 gap-2 text-sm">
          <span className="font-semibold text-zinc-400 mr-1 tracking-wide shrink-0">Digital home of:</span>
          <span className="text-zinc-600 shrink-0">|</span>

          {PORTAL_BRANDS.map(b => {
            const isActive = activeBrand === b.slug;
            return (
              <button
                key={b.slug}
                onClick={() => setBrand(isActive ? null : b.slug)}
                data-testid={`portal-brand-${b.slug}`}
                className={`px-3 py-1 rounded-full text-sm cursor-pointer transition-all whitespace-nowrap ${
                  isActive
                    ? `text-white bg-zinc-700 ${b.activeBorder} font-semibold`
                    : "text-zinc-300 hover:text-white hover:bg-zinc-700"
                }`}
              >
                {b.name}
              </button>
            );
          })}

          <Link href="/brand/future-manufacturing" data-testid="portal-brand-more">
            <span className="ml-0.5 text-zinc-500 text-sm cursor-pointer hover:text-zinc-200 whitespace-nowrap">More</span>
          </Link>

          {activeBrand && activeBrandMeta && (
            <div className="flex items-center gap-2 ml-2 px-2.5 py-0.5 rounded-full bg-zinc-800 border border-zinc-700 text-xs text-zinc-300 whitespace-nowrap" data-testid="status-brand-mode">
              <span className={`w-1.5 h-1.5 rounded-full ${PORTAL_BRANDS.find(b => b.slug === activeBrand)?.color ?? "bg-zinc-400"}`} />
              Viewing: <span className="font-semibold">{activeBrandMeta.shortName}</span> content only
              <button
                onClick={() => setBrand(null)}
                className="text-zinc-500 hover:text-white transition-colors"
                aria-label="Show all content"
                data-testid="button-clear-brand"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}

          <button
            onClick={onCollapse}
            className="ml-auto flex items-center gap-1 text-zinc-500 hover:text-zinc-200 transition-colors shrink-0"
            aria-label="Collapse brand bar"
            data-testid="button-collapse-brand-strip"
          >
            <span className="text-xs">Hide</span>
          </button>
        </div>
      </div>
    </>
  );
}
